import { useState } from "react";
import { listAccountConfigs } from "../api/config";
import { changeTier, listUsers, reactivateUser, setInUse, unsubscribeUser, updateSubscriptionEnd } from "../api/users";
import type { KiroUserListItem, ListUsersFilters } from "../api/types";
import { useAsync } from "../hooks/useAsync";
import { useAuth } from "../auth/AuthContext";
import { StatusBadge } from "../components/StatusBadge";
import { PasswordCell } from "../components/PasswordCell";
import { Pagination } from "../components/Pagination";
import { formatBeijingTime } from "../utils/datetime";

const TIERS = ["pro", "proplus", "promax", "power"];
const SUBSCRIPTION_STATUSES = ["PENDING", "ACTIVE", "CANCELED", "NEEDS_MANUAL"];
const ACTIVATION_STATUSES = [
  "PENDING_INVITE",
  "AWAITING_VERIFICATION_CODE",
  "ACTIVE",
  "MFA_REQUIRED",
  "NEEDS_MANUAL",
  "SUSPICIOUS_REJECTED",
];
const PAGE_SIZE = 20;

/** 目标账号下拉框"全部"选项对应的取值,后端 list_users_handler/export_users_handler 均识别。 */
const ALL_ACCOUNTS = "ALL";

const EMPTY_FILTERS: ListUsersFilters = {};

/** 日期字符串(YYYY-MM-DD 或含时间的 ISO)转 <input type="date"> 所需的 YYYY-MM-DD。 */
function toDateInput(dateStr?: string): string {
  if (!dateStr) return "";
  return dateStr.slice(0, 10);
}

function ExpiryEditor({
  value,
  onSave,
  disabled,
}: {
  value?: string;
  onSave: (dateStr: string) => Promise<void>;
  disabled: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [dateStr, setDateStr] = useState(toDateInput(value));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!editing) {
    return (
      <span>
        {value ?? "-"}{" "}
        {!disabled && (
          <button
            style={{ fontSize: 11, padding: "2px 6px" }}
            onClick={() => {
              setDateStr(toDateInput(value));
              setEditing(true);
              setError(null);
            }}
          >
            修改
          </button>
        )}
      </span>
    );
  }

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      <input
        type="date"
        value={dateStr}
        onChange={(e) => setDateStr(e.target.value)}
        style={{ fontSize: 12, padding: "2px 4px" }}
      />
      <button
        style={{ fontSize: 11, padding: "2px 6px" }}
        disabled={saving || !dateStr}
        onClick={async () => {
          setSaving(true);
          setError(null);
          try {
            await onSave(dateStr);
            setEditing(false);
          } catch (err) {
            setError(err instanceof Error ? err.message : "保存失败");
          } finally {
            setSaving(false);
          }
        }}
      >
        {saving ? "保存中..." : "保存"}
      </button>
      <button style={{ fontSize: 11, padding: "2px 6px" }} onClick={() => setEditing(false)} disabled={saving}>
        取消
      </button>
      {error && <span style={{ color: "#dc2626", fontSize: 11 }}>{error}</span>}
    </span>
  );
}

function TierEditor({
  currentTier,
  onSave,
  disabled,
}: {
  currentTier: string;
  onSave: (newTier: string) => Promise<void>;
  disabled: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [tier, setTier] = useState(currentTier);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!editing) {
    return (
      <button
        style={{ fontSize: 12 }}
        disabled={disabled}
        onClick={() => {
          setTier(currentTier);
          setEditing(true);
          setError(null);
        }}
      >
        升降级
      </button>
    );
  }

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      <select value={tier} onChange={(e) => setTier(e.target.value)} style={{ fontSize: 12 }}>
        {TIERS.map((t) => (
          <option key={t} value={t}>
            {t}
          </option>
        ))}
      </select>
      <button
        style={{ fontSize: 11, padding: "2px 6px" }}
        disabled={saving || tier === currentTier}
        onClick={async () => {
          if (!confirm(`确认将层级从 ${currentTier} 改为 ${tier}?`)) return;
          setSaving(true);
          setError(null);
          try {
            await onSave(tier);
            setEditing(false);
          } catch (err) {
            setError(err instanceof Error ? err.message : "升降级失败");
          } finally {
            setSaving(false);
          }
        }}
      >
        {saving ? "提交中..." : "确认"}
      </button>
      <button style={{ fontSize: 11, padding: "2px 6px" }} onClick={() => setEditing(false)} disabled={saving}>
        取消
      </button>
      {error && <span style={{ color: "#dc2626", fontSize: 11 }}>{error}</span>}
    </span>
  );
}

/** 用户列表,对应产品文档 §8.1、§7.1、§7.2、§6.3。 */
export function UsersPage() {
  const { isAdmin, canWrite } = useAuth();
  const accounts = useAsync(() => listAccountConfigs(), []);
  const [selectedAccount, setSelectedAccount] = useState<string>("");
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyEmail, setBusyEmail] = useState<string | null>(null);

  // 筛选表单草稿(未提交前的输入框内容)与已生效筛选条件分离,避免每敲一个
  // 字符就重新请求接口——只在点击"查询"或"重置"时才真正触发列表刷新。
  const [filterDraft, setFilterDraft] = useState<ListUsersFilters>(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<ListUsersFilters>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);

  const accountId = selectedAccount || accounts.data?.accounts[0]?.account_id || "";
  const users = useAsync(
    () =>
      accountId
        ? listUsers(accountId, { ...appliedFilters, page, pageSize: PAGE_SIZE })
        : Promise.resolve({ users: [], count: 0, total: 0, page: 1, page_size: PAGE_SIZE, total_pages: 0 }),
    [accountId, appliedFilters, page]
  );

  const handleApplyFilters = (e: React.FormEvent) => {
    e.preventDefault();
    setAppliedFilters(filterDraft);
    setPage(1); // 筛选条件变化后回到第一页,避免停留在一个筛选后已经不存在的页码
  };

  const handleResetFilters = () => {
    setFilterDraft(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setPage(1);
  };

  // 切换目标账号时筛选条件和页码也应该重置,不然容易出现"换了账号但筛选
  // 条件还是上一个账号设的、页码停在一个可能超出新账号数据范围的位置"。
  const handleAccountChange = (value: string) => {
    setSelectedAccount(value);
    setFilterDraft(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setPage(1);
  };

  // 选择"全部"时列表按 account_id 聚合多个账号的数据,退订/升降级/改到期日
  // 这几个操作都需要按行使用该用户实际所属的 account_id,不能用下拉框选中值
  // (下拉框选中值此时是 "ALL",不是任何一个真实账号)。
  const handleUnsubscribe = async (userAccountId: string, email: string) => {
    if (!confirm(`确认退订 ${email}?此操作会移出订阅组,当月费用仍全额收取(§7.1)。`)) return;
    setBusyEmail(email);
    setActionError(null);
    try {
      await unsubscribeUser(userAccountId, email);
      users.reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "退订失败");
    } finally {
      setBusyEmail(null);
    }
  };

  const handleReactivate = async (userAccountId: string, email: string) => {
    setBusyEmail(email);
    setActionError(null);
    try {
      await reactivateUser(userAccountId, email);
      users.reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "启用失败");
    } finally {
      setBusyEmail(null);
    }
  };

  const handleChangeTier = async (userAccountId: string, email: string, newTier: string) => {
    setBusyEmail(email);
    setActionError(null);
    try {
      await changeTier(userAccountId, email, newTier);
      users.reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "升降级失败");
      throw err;
    } finally {
      setBusyEmail(null);
    }
  };

  const handleUpdateExpiry = async (userAccountId: string, email: string, dateStr: string) => {
    setBusyEmail(email);
    setActionError(null);
    try {
      await updateSubscriptionEnd(userAccountId, email, dateStr);
      users.reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "更新到期日失败");
      throw err;
    } finally {
      setBusyEmail(null);
    }
  };

  const handleToggleInUse = async (userAccountId: string, email: string, nextValue: boolean) => {
    setBusyEmail(email);
    setActionError(null);
    try {
      await setInUse(userAccountId, email, nextValue);
      users.reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "更新使用中状态失败");
    } finally {
      setBusyEmail(null);
    }
  };

  return (
    <div>
      <h2 className="section-title">用户列表</h2>

      <div className="form-row">
        <label>目标账号</label>
        <select value={accountId} onChange={(e) => handleAccountChange(e.target.value)}>
          <option value={ALL_ACCOUNTS}>全部</option>
          {accounts.data?.accounts.map((a) => (
            <option key={a.account_id} value={a.account_id}>
              {a.account_id}
            </option>
          ))}
        </select>
      </div>

      <form onSubmit={handleApplyFilters} className="card" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
          <div className="form-row" style={{ margin: 0 }}>
            <label>用户名</label>
            <input
              value={filterDraft.username ?? ""}
              onChange={(e) => setFilterDraft({ ...filterDraft, username: e.target.value })}
              placeholder="子串匹配"
              style={{ width: 140 }}
            />
          </div>
          <div className="form-row" style={{ margin: 0 }}>
            <label>邮箱</label>
            <input
              value={filterDraft.email ?? ""}
              onChange={(e) => setFilterDraft({ ...filterDraft, email: e.target.value })}
              placeholder="子串匹配"
              style={{ width: 180 }}
            />
          </div>
          <div className="form-row" style={{ margin: 0 }}>
            <label>层级</label>
            <select
              value={filterDraft.tier ?? ""}
              onChange={(e) => setFilterDraft({ ...filterDraft, tier: e.target.value || undefined })}
            >
              <option value="">全部</option>
              {TIERS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <div className="form-row" style={{ margin: 0 }}>
            <label>订阅状态</label>
            <select
              value={filterDraft.subscriptionStatus ?? ""}
              onChange={(e) => setFilterDraft({ ...filterDraft, subscriptionStatus: e.target.value || undefined })}
            >
              <option value="">全部</option>
              {SUBSCRIPTION_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div className="form-row" style={{ margin: 0 }}>
            <label>激活状态</label>
            <select
              value={filterDraft.activationStatus ?? ""}
              onChange={(e) => setFilterDraft({ ...filterDraft, activationStatus: e.target.value || undefined })}
            >
              <option value="">全部</option>
              {ACTIVATION_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div className="form-row" style={{ margin: 0 }}>
            <label>使用中</label>
            <select
              value={filterDraft.inUse ?? ""}
              onChange={(e) => setFilterDraft({ ...filterDraft, inUse: e.target.value || undefined })}
            >
              <option value="">全部</option>
              <option value="true">是</option>
              <option value="false">否</option>
            </select>
          </div>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
          <div className="form-row" style={{ margin: 0 }}>
            <label>创建时间</label>
            <input
              type="date"
              value={filterDraft.createdFrom ?? ""}
              onChange={(e) => setFilterDraft({ ...filterDraft, createdFrom: e.target.value || undefined })}
            />
            <span>至</span>
            <input
              type="date"
              value={filterDraft.createdTo ?? ""}
              onChange={(e) => setFilterDraft({ ...filterDraft, createdTo: e.target.value || undefined })}
            />
          </div>
          <div className="form-row" style={{ margin: 0 }}>
            <label>到期时间</label>
            <input
              type="date"
              value={filterDraft.expiryFrom ?? ""}
              onChange={(e) => setFilterDraft({ ...filterDraft, expiryFrom: e.target.value || undefined })}
            />
            <span>至</span>
            <input
              type="date"
              value={filterDraft.expiryTo ?? ""}
              onChange={(e) => setFilterDraft({ ...filterDraft, expiryTo: e.target.value || undefined })}
            />
          </div>
          <button type="submit">查询</button>
          <button type="button" onClick={handleResetFilters}>
            重置
          </button>
        </div>
      </form>

      {actionError && <div className="error-banner">{actionError}</div>}
      {users.loading && <p>加载中...</p>}
      {users.error && <div className="error-banner">{users.error}</div>}

      {users.data && (
        <div className="card">
          <table>
            <thead>
              <tr>
                {accountId === ALL_ACCOUNTS && <th>账号 ID</th>}
                <th>邮箱</th>
                <th>用户名</th>
                <th>层级</th>
                <th>订阅状态</th>
                <th>激活状态</th>
                <th>密码</th>
                <th>创建时间</th>
                <th>到期日</th>
                <th>使用中</th>
                {canWrite && <th>操作</th>}
              </tr>
            </thead>
            <tbody>
              {users.data.users.map((u: KiroUserListItem) => (
                <tr key={`${u.account_id}#${u.email}`}>
                  {accountId === ALL_ACCOUNTS && <td>{u.account_id}</td>}
                  <td>{u.email}</td>
                  <td>{u.username}</td>
                  <td>{u.tier}</td>
                  <td>
                    <StatusBadge status={u.subscription_status} />
                  </td>
                  <td>
                    <StatusBadge status={u.activation_status} />
                  </td>
                  <td>
                    <PasswordCell accountId={u.account_id} email={u.email} canReveal={isAdmin} />
                  </td>
                  <td style={{ fontSize: 12 }}>{formatBeijingTime(u.created_at)}</td>
                  <td>
                    <ExpiryEditor
                      value={u.subscription_end_at}
                      disabled={!canWrite || busyEmail === u.email}
                      onSave={(dateStr) => handleUpdateExpiry(u.account_id, u.email, dateStr)}
                    />
                  </td>
                  <td>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                      {u.in_use ? "是" : "否"}
                      {canWrite && (
                        <button
                          style={{ fontSize: 11, padding: "2px 6px" }}
                          disabled={busyEmail === u.email}
                          onClick={() => handleToggleInUse(u.account_id, u.email, !u.in_use)}
                        >
                          切换
                        </button>
                      )}
                    </span>
                  </td>
                  {canWrite && (
                    <td>
                      <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                        <TierEditor
                          currentTier={u.tier}
                          // 修复:退订(CANCELED)状态下用户已不在任何订阅组内,升降级
                          // 操作(加入新组+移出旧组)在这个状态下没有意义,之前漏了这个
                          // 条件,只在“正在处理中”时禁用,导致退订用户仍能点升降级。
                          disabled={busyEmail === u.email || u.subscription_status === "CANCELED"}
                          onSave={(newTier) => handleChangeTier(u.account_id, u.email, newTier)}
                        />
                        {u.subscription_status === "CANCELED" ? (
                          <button
                            onClick={() => handleReactivate(u.account_id, u.email)}
                            disabled={busyEmail === u.email}
                          >
                            {busyEmail === u.email ? "启用中..." : "启用"}
                          </button>
                        ) : (
                          <button
                            className="danger"
                            onClick={() => handleUnsubscribe(u.account_id, u.email)}
                            disabled={busyEmail === u.email}
                          >
                            退订
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {users.data.users.length === 0 && <p style={{ color: "#64748b" }}>没有符合条件的用户</p>}
          <Pagination
            page={users.data.page}
            totalPages={users.data.total_pages}
            total={users.data.total}
            onPageChange={setPage}
          />
        </div>
      )}
    </div>
  );
}
